import type {
  AnimationConfig,
  AudioConfig,
  ModelConfig,
  PlacementConfig,
  SceneConfig,
} from './manifest.types'

/**
 * Every key except `model.url` has a default, so the minimum valid manifest is:
 *
 *   { "model": { "url": "/models/whatever.glb" } }
 *
 * That matters for swappability: a designer replacing the model should not have
 * to understand, or correctly restate, options they do not want to change.
 */

/**
 * Used when `intro` is present but omits `crossFadeSeconds`.
 *
 * Deliberately small. An intro clip is normally authored to end exactly on the
 * main clip's frame 0, so a long blend would only smear the handover; this is
 * here to cover the case where it is not.
 */
export const DEFAULT_INTRO_CROSSFADE_SECONDS = 0.1

/**
 * Used when `greeting` is present but omits `delaySeconds`.
 *
 * 0.15 s, and the number does two jobs. It is long enough to read as a BEAT -
 * the character lands, settles, and then greets, rather than bowing on the way
 * down - and it clears `DEFAULT_INTRO_CROSSFADE_SECONDS` above, so the
 * intro -> main handover has finished by the time the additive layer starts and
 * the bow lands on a fully-weighted main clip instead of on a blend of two
 * actions. Below 0.1 those two overlap. Nothing breaks there - additive
 * accumulation runs after the weighted blend either way - but the greeting's
 * first frames then ride a pose that is still moving, which is a hard thing to
 * diagnose from a phone and a trivial one to avoid here.
 */
export const DEFAULT_GREETING_DELAY_SECONDS = 0.15

export const DEFAULT_ANIMATION: AnimationConfig = {
  clip: null,
  loop: 'repeat',
  // No entrance animation unless the manifest asks for one, so a model that
  // does not have one keeps playing from load exactly as before.
  intro: null,
  // Both off unless the manifest names a clip - the same call `intro` makes.
  // They are ADDITIVE layers, and layering a clip that was not authored for it
  // is the one way to make a model look broken rather than merely plain. With
  // both null the animation controller behaves exactly as it did before they
  // existed, which is the zero-risk default for any campaign already in the
  // field.
  blink: null,
  reaction: null,
  // Off unless the manifest names a clip, the same call the two above make - and
  // for one more reason of its own. This one puts the character on a TIMELINE,
  // and handing a schedule to a campaign that never asked for one is the same
  // mistake `leadIn: 'none'` below exists to avoid.
  greeting: null,
  timeScale: 1,
}

export const DEFAULT_MODEL: Omit<ModelConfig, 'url'> = {
  targetHeightMeters: 1.5,
  yawDegrees: 0,
  yOffsetMeters: 0,
  castShadow: true,
  animation: DEFAULT_ANIMATION,
}

export const DEFAULT_SCENE: SceneConfig = {
  environment: 'room',
}

/** Used when an `audio` slot is present but omits `volume`. */
export const DEFAULT_SOUND_VOLUME = 0.8

/**
 * Silent, every slot, and there is no `ENABLE_SOUND` anywhere to go with it -
 * a slot with no URL is already the off switch, and a second way to mute one
 * thing is the shape this manifest keeps refusing to grow.
 *
 * The consequence is worth stating: a campaign that names no files never
 * constructs an AudioContext, never installs an unlock listener and never
 * issues a fetch that could only fail. That is the same posture
 * `DEFAULT_ANIMATION.intro` takes, and it is why "runs silent" is a supported
 * configuration rather than a degraded one.
 */
export const DEFAULT_AUDIO: AudioConfig = {
  step: null,
  arrive: null,
  shutter: null,
  // The empty map is `react`'s spelling of the same off switch, and it keeps
  // the invariant above intact: no entries, no fetch, no AudioContext.
  react: {},
}

export const DEFAULT_PLACEMENT: PlacementConfig = {
  // On by default: a model that pops into existence with no contact cue is the
  // problem this exists to fix, and a campaign that wants it plain says 'none'.
  effect: 'landing',
  // Off by default, unlike `effect`. The lead-in makes the user wait, and a
  // delay is not something to hand a campaign that never asked for one - so
  // every manifest written before this key existed keeps placing bit-for-bit as
  // it did. It also only pays off with an intro clip to reveal into, which is a
  // per-campaign fact this file cannot know.
  leadIn: 'none',
  // Read the hue off the GLB by default: dropping in a new model should get a
  // matching effect without anyone editing this file, which is the same bargain
  // `targetHeightMeters` makes about size.
  accentColorSource: 'model',
  // Only reached when extraction finds nothing usable. White is the safe answer
  // there - it reads over any camera background and cannot clash with a model
  // whose colours we failed to read.
  accentColor: '#ffffff',
}
